import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  ColumnSizingState,
  getCoreRowModel,
  TableOptions,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

import { ShootingDayTable } from './shooting-day-table';
import { UnscheduledShotsTable } from './unscheduled-shots-table';
import { openModal } from '../../../core/modal/open-modal';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { CountDragPreview } from '../../schedule/components/count-drag-preview';
import { useSelection } from '../../../core/contexts/selection.context';
import { StripboardTableHeader } from './stripboard-table-header';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { Project, Shot, Step, type ShootingDay, type Stripboard } from '../../../app/entities';

import Styles from '../stripboard.module.scss';
import { debounce } from 'lodash';
import { useCurrentStep } from '../../../hooks/use-current-step';

type StripboardProps = {
  stripboard: Stripboard;
  // TODO: Remove props that can be caught from stripboard
  shootingDays: ShootingDay[];
  project: Project;
  columns: ColumnDef<any>[];
  stripIds: string[];
  sortableStripIds: string[];
  totalShootingDays: number;
  unscheduledStrips: Stripboard['unscheduledStrips'];
  hideUnscheduledTable?: boolean;
  onUpdateStrip?: (
    stripId: string,
    update: {
      data?: {
        text?: string;
        estimatedTime?: number;
        isHidden?: boolean;
      };
    },
  ) => Promise<void>;
  onResizeColumn?: (propertyId: string, value: number) => void;
  onResortStrips?: (stripIds: string[], newPosition: number) => Promise<void>;
  onReorderColumn?: (propertyId: string, oldIndex: number, newIndex: number) => void;
  onAddShotToShootingDay?: (shootingDay: ShootingDay) => void;
  getStripPosition?: (stripIndex: number) => number | undefined;

  /**
   * Set to true id the table should scroll in the screen,
   * false if it should stay inline
   * default = true
   */
  scroll?: boolean;
};

export const SortableStripboard: React.FC<StripboardProps> = observer(
  ({
    stripboard,
    shootingDays,
    project,
    columns,
    stripIds,
    sortableStripIds,
    totalShootingDays,
    unscheduledStrips,
    hideUnscheduledTable,
    onReorderColumn,
    onResizeColumn,
    onResortStrips,
    onUpdateStrip,
    onAddShotToShootingDay,
    getStripPosition,
    scroll = true,
  }) => {
    const { t } = useTranslation();
    const { elements: selectedStripIds, select, clear } = useSelection();
    const { _id: stepId } = useCurrentStep();

    const [columnSizing, setColumnSizing] = React.useState({});
    const [activeId, setActiveId] = useState<string | null>(null);

    /**
     * Remove local column sizing when navigating between shot list steps
     */
    useEffect(() => {
      setColumnSizing({});
    }, [columns]);

    const activeIndex = activeId ? stripIds?.indexOf(activeId) : -1;

    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 200,
          tolerance: 5,
        },
      }),
    );

    const debouncedOnResizeColumn = useCallback(
      debounce((value) => {
        const columnToResize = value({});
        const columnId = Object.keys(columnToResize)[0];

        if (!columnId) return;

        onResizeColumn(columnId, columnToResize[columnId]);
      }, 500),
      [],
    );

    const onColumnSizingChange = (value: Updater<ColumnSizingState>) => {
      setColumnSizing(value);
      debouncedOnResizeColumn(value);
    };

    const tableOptions: Omit<TableOptions<any>, 'data'> = {
      columns,
      columnResizeMode: 'onChange',
      columnResizeDirection: 'ltr',
      getCoreRowModel: getCoreRowModel(),
      getRowId: (row) => row._id,
      meta: {
        projectId: project._id,
        onUpdateStrip,
      },
      state: {
        columnSizing,
      },
      onColumnSizingChange,
    };

    const unassignedShotsTable = useReactTable({
      data: unscheduledStrips,
      ...tableOptions,
    });

    const handleAddShot = async () => {
      if (!project.canAddShot()) {
        openModal(LimitReachedModal, {
          type: 'shots',
          eventName: 'Shots exceed 10',
        });

        return;
      }

      await Shot.createShot({
        projectId: project._id,
        position: project.shots.length,
      });
    };

    const handleAddShootingDay = async () => {
      const projectId = project._id;
      if (!projectId) {
        return;
      }

      Step.createShootingDay({
        projectId,
        stepId,
      });
    };

    const handleDragStart = ({ active }: DragStartEvent) => {
      select?.(active.id.toString());
      setActiveId(active.id.toString());
    };

    const handleDragCancel = () => {
      setActiveId(null);
    };

    const handleDragEnd = (event: DragEndEvent) => {
      const { over } = event;

      if (!over) return;

      if (over.data.current?.type === 'row') {
        const overIndex = stripIds.indexOf(`${over.id}`);

        if (activeIndex !== overIndex) {
          const newPosition = getStripPosition?.(overIndex);

          if (newPosition === undefined) {
            return;
          }

          onResortStrips?.(selectedStripIds, newPosition);
        }

        clear();
        setActiveId(null);
      }

      if (over.data.current?.type === 'shooting-day') {
        if (over) {
          const targetDayId = over.data?.current?.daybreakId || '';
          const overIndex = stripIds.indexOf(targetDayId);

          if (activeIndex !== overIndex) {
            let destinationIndex = overIndex;

            if (activeIndex < overIndex) {
              destinationIndex -= 1;
            }

            const newPosition = getStripPosition?.(destinationIndex);

            if (newPosition === undefined) {
              return;
            }

            onResortStrips?.(selectedStripIds, newPosition);
          }
        }
      }

      if (over.data.current?.type === 'unscheduled-table') {
        if (over) {
          const targetDayId = over.data?.current?.daybreakId || '';
          const overIndex = stripIds.indexOf(targetDayId);

          if (activeIndex !== overIndex) {
            onResortStrips?.(selectedStripIds, stripboard.strips.length);
          }
        }

        clear();
        setActiveId(null);
      }
    };

    const totalSize = unassignedShotsTable?.getTotalSize() + 60 || '100%';
    const selectedStripsCount = selectedStripIds?.length || 0;

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className={Styles.tableWrapper} data-scroll={scroll}>
          <StripboardTableHeader
            totalSize={totalSize}
            headerGroups={unassignedShotsTable?.getHeaderGroups()}
            columns={columns}
            onReorderColumn={onReorderColumn}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={sortableStripIds} strategy={verticalListSortingStrategy}>
              {shootingDays?.length ? (
                shootingDays?.map((shootingDay, index) => (
                  <ShootingDayTable
                    key={shootingDay._id}
                    stripboard={stripboard}
                    shootingDay={shootingDay}
                    totalShootingDays={totalShootingDays}
                    totalSize={totalSize}
                    onAddShot={() => onAddShotToShootingDay(shootingDay)}
                    tableOptions={tableOptions}
                    projectId={project._id}
                    onUpdateStrip={onUpdateStrip}
                  />
                ))
              ) : (
                <PagePlaceholder
                  backgroundImgUrl={null}
                  description="To schedule your shots and create a call sheet, you have to create a shooting day."
                  actionButton={{ text: 'Create Shooting day', onClick: handleAddShootingDay }}
                />
              )}

              {!hideUnscheduledTable && (
                <UnscheduledShotsTable
                  stripboard={stripboard}
                  projectId={project._id}
                  totalSize={totalSize}
                  table={unassignedShotsTable}
                  onAddShot={handleAddShot}
                  onUpdateStrip={onUpdateStrip}
                />
              )}
            </SortableContext>

            <DragOverlay
              dropAnimation={null}
              style={{ cursor: 'grabbing' }}
              modifiers={[snapCenterToCursor]}
            >
              {activeId ? (
                <div className={Styles.overlayWrapper}>
                  <CountDragPreview
                    count={selectedStripsCount}
                    text={t('common:strip', { count: selectedStripsCount })}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </DndContext>
    );
  },
);
